<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useAsyncData, useRouter } from '#app';
import { useRequest } from '@/composables/useRequest';
import { useI18n } from 'vue-i18n';
import NewsCard from '@/components/cards/NewsCard.vue';
const props = defineProps({
  tabs: {
    type: Array,
    default: () => [],
  },
  backgroundColor: {
    type: String,
    default: () => '',
  },
});
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const activeTab = ref(route.query.cat ? route.query.cat : 'all');
const currentPage = computed(() => Number(route.query.page) || 1);
const activeTagValue = ref(route.query.tag ? route.query.tag : 'all');
const toggleTag = (value) => {
  activeTagValue.value = activeTagValue.value === value ? 'all' : value;
};

const buildRequestParams = () => {
  const params = {};

  if (currentPage.value > 1) params.page = currentPage.value;
  if (activeTagValue.value !== 'all') params.tag = activeTagValue.value;
  if (activeTab.value !== 'all') params.category_ids = activeTab.value;

  return params;
};

const { data: response, pending } = await useAsyncData(
  'news-list',
  () =>
    useRequest('news', {
      params: buildRequestParams(),
    }),
  {
    watch: [currentPage, activeTagValue, activeTab],
  },
);

const categories = computed(() => {
  const tabs = props.tabs || [];
  return [{ title: t('promotion.all_categories'), id: 'all' }, ...tabs];
});
const fetchedTags = computed(() => response.value?.tags || []);
const fetchedItems = computed(() => response.value?.items || []);
const paginationData = computed(() => response.value?.pagination || null);

const getPageQuery = (page) => {
  const query = { ...route.query };
  if (page <= 1) delete query.page;
  else query.page = page;

  if (activeTagValue.value === 'all') delete query.tag;
  else query.tag = activeTagValue.value;

  if (activeTab.value === 'all') delete query.cat;
  else query.cat = activeTab.value;
  return query;
};

const visiblePages = computed(() => {
  if (!paginationData.value) return [];
  const last = paginationData.value.last_page;
  const current = currentPage.value;
  const range = [];
  const delta = 1;

  for (let i = 1; i <= last; i++) {
    if (
      i === 1 ||
      i === last ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i);
    } else if (range.length > 0 && range[range.length - 1] !== '...') {
      range.push('...');
    }
  }
  return range;
});
const updateQuery = () => {
  const query = {};

  if (activeTab.value !== 'all') query.cat = activeTab.value;
  if (activeTagValue.value !== 'all') query.tag = activeTagValue.value;

  router.push({ query });
};
watch(activeTab, () => {
  activeTagValue.value = 'all';
  updateQuery();
});

watch(activeTagValue, () => {
  updateQuery();
});
const tabListElement = useTemplateRef('tabList');

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};
onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});
</script>

<template>
  <div class="news__wrap">
    <div
      v-if="pending"
      :class="[
        'news__overlay',
        backgroundColor ? `news__overlay--${backgroundColor}` : '',
      ]"
      aria-hidden="true"
    >
      <div class="news__overlay-spinner"></div>
    </div>
    <Tabs v-model:value="activeTab" class="news__tabs" :scrollable="true">
      <TabList
        ref="tabList"
        :class="`news__tabs-headers tabs-bg-${backgroundColor}`"
        data-aos="fade-up"
        data-aos-easing="ease-out-cubic"
      >
        <Tab
          v-for="tab in categories"
          :key="tab.id"
          :value="String(tab.id)"
          class="news__tabs-title"
        >
          {{ tab.title }}
        </Tab>
      </TabList>
      <TabPanels
        class="news__tabs-content"
        data-aos="fade-up"
        data-aos-easing="ease-out-cubic"
      >
        <TabPanel
          v-for="tab in categories"
          :key="tab.id"
          :value="String(tab.id)"
        >
          <div v-if="fetchedTags && fetchedTags.length > 0" class="news__tags">
            <ScrollPanel style="width: 100%" class="news__tags-items">
              <Tag
                value="#Всі"
                :class="[
                  'news__tags-item link-normal button-transparent cursor-pointer',
                  activeTagValue === 'all' ? 'news__tags-item--active' : '',
                  backgroundColor === 'black'
                    ? 'news__tags-item--white'
                    : 'news__tags-item--black',
                ]"
                data-aos="fade-up"
                :data-aos-delay="100"
                @click="activeTagValue = 'all'"
              />

              <Tag
                v-for="(tag, tagIndex) in fetchedTags"
                :key="tag.value"
                :value="`#${tag.label}`"
                :class="[
                  'news__tags-item link-normal button-transparent cursor-pointer',
                  activeTagValue === tag.value ? 'news__tags-item--active' : '',
                  backgroundColor === 'black'
                    ? 'news__tags-item--white'
                    : 'news__tags-item--black',
                ]"
                data-aos="fade-up"
                :data-aos-delay="tagIndex >= 1 ? 100 + 100 * tagIndex : 100"
                @click="toggleTag(tag.value)"
              />
            </ScrollPanel>
          </div>
          <div class="news__items">
            <template
              v-for="(newsItem, newsItemIndex) in fetchedItems"
              :key="newsItemIndex"
            >
              <NewsCard
                :image="newsItem.image"
                :tags="newsItem.tags"
                :title="newsItem.title"
                :date="newsItem.date"
                :url="newsItem.url"
                :theme="backgroundColor"
                :data-aos-delay="
                  newsItemIndex >= 1 ? 100 + 100 * newsItemIndex : 100
                "
              />
            </template>
          </div>
          <nav
            v-if="paginationData && paginationData.last_page > 1"
            class="news__pagination"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          >
            <NuxtLink
              :to="{ query: getPageQuery(currentPage - 1) }"
              class="news__pagination-btn news__pagination-btn--prev"
              :class="{ 'news__pagination-btn--disabled': currentPage === 1 }"
              rel="prev"
            >
              <CIcon
                class="news__pagination-btn-icon"
                name="direction-black"
                width="16"
                height="16"
              />
            </NuxtLink>

            <div class="news__pagination-items">
              <template v-for="(page, idx) in visiblePages" :key="idx">
                <span
                  v-if="page === '...'"
                  class="news__pagination-item news__pagination-item--dots"
                  >...</span
                >
                <NuxtLink
                  v-else
                  :to="{ query: getPageQuery(page) }"
                  :class="[
                    'news__pagination-item',
                    { 'news__pagination-item--active': page === currentPage },
                  ]"
                >
                  {{ page }}
                </NuxtLink>
              </template>
            </div>

            <NuxtLink
              :to="{ query: getPageQuery(currentPage + 1) }"
              class="news__pagination-btn news__pagination-btn--next"
              :class="{
                'news__pagination-btn--disabled':
                  currentPage === paginationData.last_page,
              }"
              rel="next"
            >
              <CIcon
                class="news__pagination-btn-icon"
                name="direction-black"
                width="16"
                height="16"
              />
            </NuxtLink>
          </nav>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<style scoped lang="scss">
@keyframes catalog-spin {
  to {
    transform: rotate(360deg);
  }
}
.news {
  $parent: &;
  &__overlay {
    inset: 0;
    pointer-events: all;
    position: absolute;
    z-index: 5;
    &--white {
      background: rgba($white, 0.65);
    }
    &--gray {
      background: rgba($gray, 0.65);
    }
    &--black {
      background: rgba($black, 0.65);
    }
  }

  &__overlay-spinner {
    animation: catalog-spin 0.8s linear infinite;
    border: 3px solid $divider;
    border-radius: 50%;
    border-top-color: $black;
    height: 40px;
    margin: 0 auto;
    position: sticky;
    top: calc(50% - 20px);
    width: 40px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 3);
      @include responsive-spacing(height, 40);
      @include responsive-spacing(width, 40);
      top: calc(50vh - px-to-vw(20));
    }
  }
  &__wrap {
    gap: 40px;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  &__tags {
    margin-left: -16px;
    width: calc(100% + 32px);
    @include media-breakpoint-up(md) {
      margin-left: 0;
      width: 100%;
    }
    &-item {
      background-color: transparent;
      border: 1px solid $outline;
      border-radius: 48px;
      cursor: pointer;
      min-width: fit-content;
      padding: 7px 12px;
      transition: all 0.3s linear;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(border-radius, 48);
        @include responsive-spacing(padding-top, 7);
        @include responsive-spacing(padding-bottom, 7);
        @include responsive-spacing(padding-left, 12);
        @include responsive-spacing(padding-right, 12);
      }
      &--white {
        color: $white !important;
      }
      &--black {
        color: $black !important;
      }
      &--active {
        background-color: $black;
        color: $white !important;
      }
    }
    &:deep(.p-scrollpanel-content) {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      gap: 8px;
      padding: 0 16px;
      @include media-breakpoint-up(sm) {
        justify-content: center;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-right, 16);
      }
    }
  }
  &__items {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(2, 6fr);
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(3, 4fr);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
    &--empty {
      text-align: center;
    }
  }
  &__tabs {
    flex: 1 1 auto;
    min-width: 0;
    @include media-breakpoint-up(sm) {
      display: flex;
    }
    &-headers {
      display: flex;
      position: relative;
      &:deep(.p-tablist-content) {
        border-bottom: 1px solid $divider;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-bottom-width, 1);
        }
      }
      &:deep(.p-tablist-tab-list) {
        gap: 8px;
        @include media-breakpoint-up(sm) {
          justify-content: center;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
        }
      }
      &:deep(.p-tablist-active-bar) {
        height: 4px;
      }
      @include media-breakpoint-up(sm) {
        justify-content: center;
      }
      @include media-breakpoint-up(xl) {
        &:deep(.p-tablist-active-bar) {
          @include responsive-spacing(height, 4);
        }
      }
    }
    &-title {
      @include link-normal;
      padding: 16px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding, 16);
      }
    }
    &-content {
      padding-top: 24px;
      @include media-breakpoint-up(sm) {
        padding-top: 40px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 40);
      }
      &:deep(.p-tabpanel) {
        display: flex;
        flex-direction: column;
        gap: 48px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 48);
        }
      }
    }
  }
  &__pagination {
    align-items: center;
    display: flex;
    justify-content: center;
    gap: 12px;
    padding: 25px 16px 24px;
    @include media-breakpoint-up(sm) {
      gap: 28px;
      padding: 41px 48px 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 28);
      @include responsive-spacing(padding-top, 41);
      @include responsive-spacing(padding-left, 48);
      @include responsive-spacing(padding-right, 48);
      @include responsive-spacing(padding-bottom, 40);
    }
    &-items {
      align-items: center;
      display: flex;
      gap: 4px;
      list-style: none;
      margin: 0;
      padding: 0;
      @include media-breakpoint-up(sm) {
        gap: 12px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 12);
      }
    }
    &-btn {
      align-items: center;
      display: flex;
      justify-content: center;
      height: 32px;
      width: 32px;
      @include media-breakpoint-up(sm) {
        border: 1px solid $outline !important;
        border-radius: 50%;
        height: 48px;
        width: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(height, 48);
        @include responsive-spacing(width, 48);
      }
      &--disabled {
        pointer-events: none;
        opacity: 0.5;
      }
      &--prev {
        #{$parent} {
          &__pagination-btn {
            &-icon {
              transform: rotate(180deg);
            }
          }
        }
      }
    }
    &-item {
      margin: 0;
      padding: 0;
      @include link-normal;
      align-items: center;
      border-radius: 50%;
      display: inline-flex;
      justify-content: center;
      height: 32px;
      min-width: 24px;
      text-decoration: none;
      @include media-breakpoint-up(sm) {
        border: 1px solid $divider;
        height: 48px;
        min-width: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(height, 48);
        @include responsive-spacing(min-width, 48);
      }

      &--active {
        background-color: $black;
        border-radius: 50%;
        color: $white;
        min-width: 32px;
        @include media-breakpoint-up(sm) {
          border: 1px solid $black;
          min-width: 48px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-width, 1);
          @include responsive-spacing(min-width, 48);
        }
      }
      &--dots {
        @include link-normal;
        display: inline-flex;
        min-width: 24px;
        justify-content: center;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(min-width, 24);
        }
      }
    }
  }
}
</style>
